import { useEffect, useRef, useState } from "react";
import { Camera, Trash } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const EMPTY = { name: "", category: "Other", sku: "", quantity: 0, min_threshold: 0, unit: "pcs", unit_price: "", supplier: "", location: "", notes: "", image_path: "" };
const API = process.env.REACT_APP_BACKEND_URL + "/api";
const NEW_CAT = "__new__";

export default function ItemDialog({ open, onOpenChange, item, categories, onSaved, onCategoryAdded }) {
  const { settings } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [newCat, setNewCat] = useState(null);
  const [cats, setCats] = useState(categories);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState("");
  const fileRef = useRef(null);
  const editing = Boolean(item);

  useEffect(() => { setCats(categories); }, [categories]);

  useEffect(() => {
    if (open) { setForm(item ? { ...EMPTY, ...item } : EMPTY); setNewCat(null); setPreview(""); }
  }, [open, item]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB"); return; }
    setUploading(true);
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_path", data.path);
    } catch (err) {
      setPreview("");
      toast.error(formatApiError(err.response?.data?.detail) || "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const imageSrc = preview || (form.image_path ? `${API}/files/${form.image_path}` : "");

  const addCategory = async () => {
    const name = (newCat || "").trim();
    if (!name) { toast.error("Category name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/categories", { name });
      setCats((c) => (c.includes(data.name) ? c : [...c.filter((x) => x !== "Other"), data.name, ...(c.includes("Other") ? ["Other"] : [])]));
      set("category", data.name);
      setNewCat(null);
      toast.success(`Category "${data.name}" added`);
      onCategoryAdded?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    const payload = {
      ...form,
      quantity: Number(form.quantity) || 0,
      min_threshold: Number(form.min_threshold) || 0,
      unit_price: Number(form.unit_price) || 0,
    };
    try {
      if (editing) await api.put(`/inventory/${item.id}`, payload);
      else await api.post("/inventory", payload);
      toast.success(editing ? "Item updated" : "Item added");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="item-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "Edit item" : "Add new item"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2 flex items-center gap-4">
            <button type="button" data-testid="item-image-picker" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="relative h-24 w-24 shrink-0 rounded-md border border-dashed border-border bg-muted/40 flex items-center justify-center overflow-hidden hover:border-primary transition-colors duration-150">
              {imageSrc ? <img src={imageSrc} alt="Product" className="h-full w-full object-cover" data-testid="item-image-preview" />
                : <Camera size={26} className="text-muted-foreground" />}
              {uploading && <span className="absolute inset-0 bg-background/70 flex items-center justify-center text-xs">Uploading…</span>}
            </button>
            <div className="space-y-1.5">
              <Label>Product picture <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <p className="text-xs text-muted-foreground">JPG, PNG or WEBP, up to 5 MB.</p>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" data-testid="item-image-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {imageSrc ? "Change picture" : "Upload picture"}
                </Button>
                {imageSrc && (
                  <Button type="button" size="sm" variant="ghost" data-testid="item-image-remove-btn" onClick={() => { set("image_path", ""); setPreview(""); }}>
                    <Trash size={14} /> Remove
                  </Button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="item-image-input"
                onChange={(e) => { uploadImage(e.target.files?.[0]); e.target.value = ""; }} />
            </div>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Item name</Label>
            <Input data-testid="item-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Titanium pylon tube" />
          </div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            {newCat === null ? (
              <Select value={form.category} onValueChange={(v) => (v === NEW_CAT ? setNewCat("") : set("category", v))}>
                <SelectTrigger data-testid="item-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  <SelectItem value={NEW_CAT} data-testid="item-category-new-option" className="text-primary font-medium">+ New category…</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="flex gap-1.5">
                <Input autoFocus data-testid="new-category-input" value={newCat} onChange={(e) => setNewCat(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(); } if (e.key === "Escape") setNewCat(null); }}
                  placeholder="Category name" />
                <Button type="button" size="sm" data-testid="new-category-save-btn" onClick={addCategory} disabled={busy}>Add</Button>
                <Button type="button" size="sm" variant="ghost" data-testid="new-category-cancel-btn" onClick={() => setNewCat(null)}>✕</Button>
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>SKU / Ref</Label>
            <Input data-testid="item-sku-input" value={form.sku} onChange={(e) => set("sku", e.target.value)} placeholder="Auto (RGHL-…) if blank" />
          </div>
          <div className="space-y-1.5">
            <Label>{editing ? "Quantity" : "Starting quantity"}</Label>
            <Input type="number" step="any" data-testid="item-quantity-input" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Unit</Label>
            <Input data-testid="item-unit-input" value={form.unit} onChange={(e) => set("unit", e.target.value)} placeholder="pcs, box, m" />
          </div>
          <div className="space-y-1.5">
            <Label>Min. threshold</Label>
            <Input type="number" step="any" data-testid="item-threshold-input" value={form.min_threshold} onChange={(e) => set("min_threshold", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Unit price ({settings.currency}) <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input type="number" step="any" data-testid="item-price-input" placeholder="Optional" value={form.unit_price === 0 ? "" : form.unit_price} onChange={(e) => set("unit_price", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Supplier</Label>
            <Input data-testid="item-supplier-input" value={form.supplier} onChange={(e) => set("supplier", e.target.value)} placeholder="Optional" />
          </div>
          <div className="space-y-1.5">
            <Label>Location</Label>
            <Input data-testid="item-location-input" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Shelf / bin" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Notes</Label>
            <Textarea data-testid="item-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy || uploading} data-testid="save-item-button" className="bg-primary hover:bg-primary/90">
            {busy ? "Saving…" : editing ? "Save changes" : "Add item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
